<template>
  <div
    v-if="currentScan"
    class="tw-p-[25px] tw-flex tw-flex-col tw-gap-y-[25px] tw-bg-[#f2f8ff80] tw-border tw-border-b tw-border-t-[0px] tw-border-solid tw-border-[#EFF0F1]"
    @mouseover="$emit('hoverIn')"
    @mouseleave="$emit('hoverOut')"
  >
    <ScanNavigationSpinner
      :data="currentScan"
      :scan-report-runs="props.data.scanReportRuns"
      :web-services-id="data.webserviceId || ''"
      :domain-id="data.domainId || ''"
      :is-show-next="currentIndex < totalScan - 1"
      :is-show-prev="currentIndex > 0"
      :is-show-switch-scan="currentIndex !== data.latestScanIndex"
      :scan-target-type="scanTargetType"
      @switch-scan="switchScan"
    />

    <div class="overview-container tw-flex tw-gap-[20px] tw-flex-wrap">
      <!-- Scan status -->
      <div class="tw-flex-1 tw-h-[113px]">
        <ScanStatusMiniDash
          :web-services-id="data.webserviceId || ''"
          :domain-id="data.domainId || ''"
          :data="currentScan"
          :percent-progress="
            currentScan.runStatusId === statusScanProgress.SCAN_COMPLETE
              ? 100
              : 40
          "
        />
      </div>
      <!-- Next Scan -->
      <div
        class="tw-flex-1 tw-rounded-md tw-border-solid tw-border tw-border-[#EFF0F1]"
      >
        <NextScanMiniDash :data="data" />
      </div>
      <!-- Issues over time -->
      <div
        class="tw-flex-1 tw-h-[113px] tw-bg-[#FFF] tw-rounded-md tw-border-solid tw-border tw-border-[#EFF0F1] tw-py-[20px] tw-pl-[20px]"
      >
        <issuesOverTimeMiniGraph
          :id="scanTargetType + '-' + data.id"
          :severity="data.scanReportRuns"
        />
      </div>
    </div>
    <IssueNumberBlocks
      :data="currentScan"
      :web-services-id="data.webserviceId || ''"
      :domain-id="data.domainId || ''"
      :severity="{
        critical: currentScan.critical,
        high: currentScan.high,
        medium: currentScan.med,
        low: currentScan.low,
      }"
    />
    <ScanningTypes
      v-if="statusProgress === statusScanProgress.SCAN_COMPLETE"
      :is-expand="isExpand"
      :run-id="currentScan.scanReportRunId"
      :scan-target-type="scanTargetType"
      :root-id="
        scanTargetType === TYPE_SCHEDULE.webservices
          ? data.webserviceId
          : data.domainId
      "
      :hover-bg-color="'hover:tw-bg-[#F2F8FF]'"
    />
  </div>
</template>

<script setup lang="ts">
import IssueNumberBlocks from "./IssueNumberBlocks.vue";
import NextScanMiniDash from "./nextScanMiniDash.vue";
import ScanNavigationSpinner from "./scanNavigationSpinner.vue";
import ScanStatusMiniDash from "./scanStatusMiniDash.vue";
import { type ScansHistoryWebServicesType } from "@/store/scanning";
import { computed, PropType, ref, watch } from "vue";
import { statusScanProgress, TYPE_SCHEDULE } from "@/utils/constants";
import ScanningTypes from "@/components/scanning/scanningTypes/index.vue";
import issuesOverTimeMiniGraph from "./issuesOverTimeMiniGraph.vue";

const props = defineProps({
  data: {
    type: Object as PropType<
      ScansHistoryWebServicesType & { domainId: number }
    >,
    required: true,
  },
  scanTargetType: {
    type: String as PropType<keyof typeof TYPE_SCHEDULE>,
    required: false,
    default: TYPE_SCHEDULE.webservices,
  },
  statusProgress: {
    type: String,
    required: true,
  },
  isExpand: {
    type: Boolean,
    required: false,
    default: false,
  },
});
defineEmits(["hoverIn", "hoverOut"]);
const currentIndex = ref(props.data?.latestScanIndex);

watch(
  () => props.data.latestScanIndex,
  (value) => {
    currentIndex.value = value;
  },
  {
    immediate: true,
    deep: true,
  },
);

const currentScan = computed(
  () => props.data.scanReportRuns?.[currentIndex.value],
);
const totalScan = computed(() => props.data.scanReportRuns?.length || 0);

const switchScan = (mode: "next" | "prev" | "latest") => {
  if (mode === "latest") {
    currentIndex.value = props.data.latestScanIndex;
    return;
  }

  const total = props.data.scanReportRuns.length;
  let nextIndex = currentIndex.value + (mode === "next" ? 1 : -1);
  if (nextIndex === total) {
    nextIndex = 0;
  }
  if (nextIndex === -1) {
    nextIndex = total - 1;
  }
  currentIndex.value = nextIndex;
};
</script>
